import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { encode } from 'base-64'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_PROJECT44_APP, SAVE_PROJECT44_CREDENTIALS } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type SaveEncodedCredentialsInput = {
  appType: AppTypeEnum
  encodedUsernamePassword: string
}

type EnableProject44AppInput = {
  appType: AppTypeEnum
}

const formSchema = z.object({
  appPassword: z.string().min(1, 'Password is required'),
  appUsername: z.string().min(1, 'Username is required')
})

/**
 * This is the modal for the Project44 app.
 */
export function Project44Modal({ isOpen, onClose, appType }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [showConfirmationStep, setShowConfirmationStep] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      appPassword: '',
      appUsername: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [saveProject44AppCredentials, { loading: saveCredentialsLoading }] = useMutation<
    { saveProject44AppCredentials: { id: string } },
    SaveEncodedCredentialsInput
  >(SAVE_PROJECT44_CREDENTIALS, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      setShowConfirmationStep(true)
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'InvalidCredentials'
          ? 'Invalid Project44 credentials. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    }
  })

  const [enableProject44App, { loading: enableAppLoading }] = useMutation<
    { enableProject44App: { id: string } },
    EnableProject44AppInput
  >(ENABLE_PROJECT44_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Project44 app is enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'InvalidScopes'
          ? 'Invalid Project44 scopes. Please check the documentation below. If error persists, email support at support@chaineapp.com'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    setShowConfirmationStep(false)
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const usernamePasswordString = `${values.appUsername}:${values.appPassword}`
    const base64EncodedCredentials = encode(usernamePasswordString)

    saveProject44AppCredentials({
      variables: {
        appType: appType as AppTypeEnum,
        encodedUsernamePassword: base64EncodedCredentials
      }
    })
  }

  const handleConfirmEnable = () => {
    enableProject44App({
      variables: { appType: appType as AppTypeEnum }
    })
  }

  if (showConfirmationStep) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>One more quick step!</DialogTitle>
            <DialogDescription>Project44 requires that you give us permission.</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/project44/getting-started#1-go-to-user-manage-inside-of-project44"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
              </a>
            </div>

            <div className="space-y-2">
              <p className="font-medium">Follow these steps:</p>
              <ol className="list-decimal list-inside space-y-2 pl-4">
                <li>Go to user-management inside of project44</li>
                <li>Find the newly created Chain user (search for chaine-app), and click edit</li>
                <li>
                  Add the following roles:
                  <ul className="list-disc list-inside pl-4 mt-2">
                    <li className="font-mono text-sm">Customer Admin</li>
                    <li className="font-mono text-sm">Truckload Visibility API</li>
                    <li className="font-mono text-sm">Developer</li>
                  </ul>
                </li>
                <li>Make sure to click SAVE at the top right</li>
              </ol>
            </div>

            <p className="text-sm">Once you're done, click the button below to check if it was setup correctly</p>

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter>
              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="button" onClick={handleConfirmEnable} disabled={enableAppLoading}>
                  {enableAppLoading ? 'Confirming...' : 'Confirm setup'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Project44 for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires an Admin project44 account. Enter your Admin project44 username and
            password.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="appUsername"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project44 admin username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your project44 username" {...field} disabled={saveCredentialsLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="appPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project44 admin password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      placeholder="Enter your project44 password"
                      {...field}
                      disabled={saveCredentialsLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/project44"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={saveCredentialsLoading} onClick={form.handleSubmit(onSubmit)}>
                  {saveCredentialsLoading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
